import React, { useCallback, useEffect, useState, useMemo } from "react"
import { Link, useSearchParams } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Collapse,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient from "helpers/apiClient"
import { formatIndianDateTime } from "helpers/indianDate"
import "./BiddingOverview.scss"

const OVERVIEW_URL = "/api/bids/admin/overview"

interface ListingSummary {
  _id: string
  propertyTitle?: string
  address?: string
  city?: string
  state?: string
  biddingEnabled?: boolean
  biddingStartPrice?: number
  biddingMinIncrement?: number
  auctionStartDate?: string
  auctionEndDate?: string
}

interface UserRef {
  _id: string
  fullName?: string
  email?: string
}

interface BidRow {
  _id: string
  amount: number
  isLeading?: boolean
  createdAt: string
  userId: UserRef | null
}

interface OverviewItem {
  listing: ListingSummary
  auctionStatus: string
  totalBidCount: number
  currentHighestBid: { amount: number; userId: UserRef | null; createdAt: string } | null
  bids: BidRow[]
}

interface BreadcrumbItem {
  title: string
  link: string
}

interface BiddingOverviewProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const formatCurrency = (n: number) => "₹" + (n ?? 0).toLocaleString("en-IN")
const formatDate = (d: string) => formatIndianDateTime(d, "–")

const BiddingOverview = (props: BiddingOverviewProps) => {
  const [searchParams] = useSearchParams()
  const filterUserId = searchParams.get("userId")

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<OverviewItem[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filterUserName, setFilterUserName] = useState<string | null>(null)

  useEffect(() => {
    const breadcrumbTitle = filterUserId ? `Bids by ${filterUserName || "User"}` : "Bidding Overview"
    props.setBreadcrumbItems(breadcrumbTitle, [
      { title: "Bidding", link: "#" },
      { title: filterUserId ? "User Bids" : "Overview", link: "#" },
    ])
  }, [props, filterUserId, filterUserName])

  const fetchOverview = useCallback(() => {
    setLoading(true)
    setError(null)
    apiClient
      .get<{ success: boolean; data?: OverviewItem[] }>(OVERVIEW_URL)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data?.data)) {
          setData(res.data.data)
        } else {
          setData([])
        }
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || e?.message || "Failed to load bidding overview.")
        setData([])
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchOverview()
  }, [fetchOverview])

  // Filter data if userId is provided
  const filteredData = useMemo(() => {
    if (!filterUserId) return data

    // Filter to show only properties where this user has bid
    const filtered = data
      .map((item) => {
        const userBids = item.bids.filter((b) => b.userId?._id === filterUserId)
        if (userBids.length === 0) return null
        // Get user name from first bid
        if (!filterUserName && userBids[0]?.userId?.fullName) {
          setFilterUserName(userBids[0].userId.fullName)
        }
        return {
          ...item,
          bids: userBids,
          totalBidCount: userBids.length,
        }
      })
      .filter(Boolean) as OverviewItem[]
    return filtered
  }, [data, filterUserId, filterUserName])

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  if (loading) {
    return (
      <Row>
        <Col className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading bidding overview...</p>
        </Col>
      </Row>
    )
  }

  if (error) {
    return (
      <Row>
        <Col>
          <Alert color="danger">{error}</Alert>
          <Button color="primary" onClick={fetchOverview}>Retry</Button>
        </Col>
      </Row>
    )
  }

  return (
    <div className="bidding-overview-page">
      <Row>
        <Col xl={12}>
          <Card className="mb-4">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h5" className="mb-0">
                {filterUserId ? (
                  <>
                    <i className="mdi mdi-account me-2" />
                    Bids by {filterUserName || "User"}
                  </>
                ) : (
                  "Bidding Overview"
                )}
              </CardTitle>
              <div className="d-flex gap-2">
                {filterUserId && (
                  <Link to="/bidding-overview" className="btn btn-sm btn-outline-secondary">
                    <i className="mdi mdi-close me-1" />
                    Clear Filter
                  </Link>
                )}
                <Button color="primary" size="sm" onClick={fetchOverview}>
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {filterUserId ? (
                <p className="text-muted mb-3">
                  Showing all properties where <strong>{filterUserName || "this user"}</strong> has placed bids.
                </p>
              ) : (
                <p className="text-muted mb-3">
                  Properties where bidding has taken place. Expand a row to see who placed which bid.
                </p>
              )}
              {filteredData.length === 0 ? (
                <Alert color="info">
                  {filterUserId ? "This user has not placed any bids." : "No properties with bids yet."}
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table bordered hover className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Property</th>
                        <th className="text-center">{filterUserId ? "User's Bids" : "Total Bids"}</th>
                        <th className="text-end">{filterUserId ? "User's Highest" : "Current Highest"}</th>
                        <th className="text-center">Status</th>
                        <th style={{ width: 100 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item) => {
                        const id = item.listing._id
                        const isOpen = expandedId === id
                        // Get user's highest bid if filtering by user
                        const userHighestBid = filterUserId && item.bids.length > 0
                          ? item.bids.reduce((max, b) => b.amount > max.amount ? b : max, item.bids[0])
                          : null
                        const displayHighest = filterUserId ? userHighestBid : item.currentHighestBid
                        return (
                          <React.Fragment key={id}>
                            <tr>
                              <td>
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 text-dark"
                                  onClick={() => toggleExpand(id)}
                                  aria-expanded={isOpen}
                                >
                                  <i className={`mdi mdi-chevron-${isOpen ? "down" : "right"}`} />
                                </Button>
                              </td>
                              <td>
                                <strong>{item.listing.propertyTitle || "Untitled"}</strong>
                                {(item.listing.address || item.listing.city) && (
                                  <div className="small text-muted">
                                    {[item.listing.address, item.listing.city, item.listing.state]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </div>
                                )}
                              </td>
                              <td className="text-center">
                                <Badge color="primary" className="px-2">{item.totalBidCount}</Badge>
                              </td>
                              <td className="text-end">
                                {displayHighest ? (
                                  <>
                                    <strong>{formatCurrency(displayHighest.amount)}</strong>
                                    {!filterUserId && displayHighest.userId && (
                                      <div className="small text-muted">
                                        {displayHighest.userId.fullName || displayHighest.userId.email}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  "–"
                                )}
                              </td>
                              <td className="text-center">
                                <Badge
                                  color={
                                    item.auctionStatus === "live"
                                      ? "success"
                                      : item.auctionStatus === "ended"
                                        ? "secondary"
                                        : "info"
                                  }
                                >
                                  {item.auctionStatus}
                                </Badge>
                              </td>
                              <td>
                                <Link to={`/bid/${id}`} className="btn btn-sm btn-outline-primary">
                                  View
                                </Link>
                              </td>
                            </tr>
                            <tr className="bidding-overview-detail-row">
                              <td colSpan={6} className="p-0 border-0">
                                <Collapse isOpen={isOpen}>
                                  <div className="bidding-overview-bids-table p-3 bg-light">
                                    <h6 className="mb-2">
                                      {filterUserId
                                        ? `${filterUserName || "User"}'s bids on this property`
                                        : "All bids (who bid what)"}
                                    </h6>
                                    <Table size="sm" bordered className="mb-0">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Amount</th>
                                          {!filterUserId && <th>Bidder</th>}
                                          {!filterUserId && <th>Email</th>}
                                          <th>Time</th>
                                          <th>Leading</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.bids.map((bid, idx) => (
                                          <tr key={bid._id}>
                                            <td>{idx + 1}</td>
                                            <td className="fw-semibold">{formatCurrency(bid.amount)}</td>
                                            {!filterUserId && <td>{bid.userId?.fullName ?? "–"}</td>}
                                            {!filterUserId && <td className="small">{bid.userId?.email ?? "–"}</td>}
                                            <td className="small text-muted">{formatDate(bid.createdAt)}</td>
                                            <td>{bid.isLeading ? <Badge color="success">Yes</Badge> : "–"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </Collapse>
                              </td>
                            </tr>
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

const mapDispatchToProps = { setBreadcrumbItems }
export default connect(null, mapDispatchToProps)(BiddingOverview)
